import { motion, AnimatePresence } from 'framer-motion';
import { Shield, CheckCircle, ArrowRight, FileText, Briefcase, Clock, Award, Star, ChevronLeft, ChevronRight, Building } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useContactPopup } from '@/hooks/useContactPopup';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';

const ServiceForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+44',
    phone: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const submissions = JSON.parse(localStorage.getItem('contactSubmissions') || '[]');
    submissions.push({ ...formData, timestamp: new Date().toISOString(), source: 'company-formation-page' });
    localStorage.setItem('contactSubmissions', JSON.stringify(submissions));
    toast.success('Thank you! We\'ll contact you within 24 hours to start your company formation.');
    setFormData({ name: '', email: '', countryCode: '+44', phone: '', message: '' });
  };

  const countryCodes = [
    { code: '+44', country: 'UK' },
    { code: '+1', country: 'US/Canada' },
    { code: '+91', country: 'India' },
    { code: '+61', country: 'Australia' },
    { code: '+353', country: 'Ireland' }
  ];

  return (
    <Card className="p-8 border-2 border-primary shadow-elegant-xl">
      <div className="mb-6">
        <Badge className="mb-3 bg-secondary text-secondary-foreground">Special Offer</Badge>
        <h3 className="text-2xl font-bold text-foreground mb-2">Form Your Company - Only £199</h3>
        <p className="text-muted-foreground">Complete company formation with Companies House registration. Fill out the form and we'll contact you within 24 hours.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="service-name">Name *</Label>
          <Input
            id="service-name"
            placeholder="John Smith"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="service-email">Email *</Label>
          <Input
            id="service-email"
            type="email"
            placeholder="john@example.com"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="service-phone">Contact Number *</Label>
          <div className="flex gap-2">
            <Select value={formData.countryCode} onValueChange={(value) => setFormData({ ...formData, countryCode: value })}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {countryCodes.map((item) => (
                  <SelectItem key={item.code} value={item.code}>
                    {item.code} ({item.country})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              id="service-phone"
              type="tel"
              placeholder="1234567890"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              required
              className="flex-1"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="service-message">Company Details</Label>
          <Textarea
            id="service-message"
            placeholder="Tell us about your company (proposed name, business type, number of directors, etc.)"
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            rows={4}
          />
        </div>

        <Button type="submit" className="w-full bg-gradient-to-r from-primary to-secondary hover:shadow-glow transition-all duration-300">
          Start My Company Formation
          <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </form>
    </Card>
  );
};

const TestimonialCard = ({ testimonial }) => (
  <Card className="p-6 h-full flex flex-col border-border shadow-lg hover:shadow-xl transition-shadow duration-300">
    <div className="flex gap-1 mb-4">
      {[1, 2, 3, 4, 5].map((star) => {
        const rating = testimonial.rating;
        const isFullStar = star <= rating;
        const isHalfStar = star === Math.ceil(rating) && rating % 1 !== 0;
        
        return (
          <div key={star} className="relative w-5 h-5">
            {isHalfStar ? (
              <>
                <Star className="w-5 h-5 text-gray-300 absolute inset-0" fill="currentColor" />
                <div className="overflow-hidden absolute w-2.5 h-5">
                  <Star className="w-5 h-5 text-yellow-400" fill="currentColor" />
                </div>
              </>
            ) : (
              <Star 
                className={`w-5 h-5 ${isFullStar ? 'text-yellow-400' : 'text-gray-300'}`} 
                fill="currentColor" 
              />
            )}
          </div>
        );
      })}
    </div>
    
    <p className="text-base text-foreground mb-6 leading-relaxed italic flex-1">
      &quot;{testimonial.content}&quot;
    </p>
    
    <div className="border-t border-border pt-4">
      <p className="font-semibold text-foreground">{testimonial.name}</p>
      <p className="text-sm text-muted-foreground mt-1">{testimonial.role}</p>
    </div>
  </Card>
);

export const CompanyFormationPage = () => {
  const { openPopup } = useContactPopup();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const testimonials = [
    {
      name: 'Michael Chen',
      role: 'Co-Founder, UrbanBuild Construction Ltd',
      content: 'Starting a business is overwhelming, but Trademark Assist made company formation seamless. They handled all the paperwork, ensured compliance with Companies House, and delivered our incorporation certificate within 24 hours. Outstanding service from start to finish.',
      rating: 5
    },
    {
      name: 'Sarah Mitchell',
      role: 'Founder & CEO, TechFlow Solutions',
      content: 'I was dreading the company formation process, but Trademark Assist made it incredibly simple. They explained everything clearly, handled all the legal requirements, and we were incorporated and ready to trade within a day. Highly recommend.',
      rating: 5
    },
    {
      name: 'David Williams',
      role: 'Managing Director, Elite Fashion Group',
      content: 'We needed to set up a new subsidiary quickly, and Trademark Assist delivered. Their efficient process and attention to detail meant everything was done correctly the first time. No delays, no complications, just professional service.',
      rating: 4.5
    },
    {
      name: 'Aisha Khan',
      role: 'Founder, TechStartup Hub',
      content: 'As a first-time entrepreneur, I had no idea where to start with company formation. Trademark Assist guided me through every step, answered all my questions, and handled everything while I focused on building my business. Invaluable support.',
      rating: 5
    },
    {
      name: 'James Robertson',
      role: 'Creative Director, Pixel & Canvas Studio',
      content: 'The team was fantastic—responsive, knowledgeable, and efficient. They took care of all the administrative headaches so we could focus on launching our creative agency. Worth every penny for the time and stress saved.',
      rating: 5
    }
  ];

  const getVisibleTestimonials = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const testimonialsPerView = isMobile ? 1 : 3;
    const visible = [];
    for (let i = 0; i < testimonialsPerView; i++) {
      const index = (currentTestimonial + i) % testimonials.length;
      visible.push({ ...testimonials[index], originalIndex: index });
    }
    return visible;
  };

  const nextTestimonial = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const step = isMobile ? 1 : 3;
    setCurrentTestimonial((prev) => (prev + step) % testimonials.length);
  };

  const prevTestimonial = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const step = isMobile ? 1 : 3;
    setCurrentTestimonial((prev) => (prev - step + testimonials.length) % testimonials.length);
  };

  useEffect(() => {
    const testimonialInterval = setInterval(() => {
      nextTestimonial();
    }, 6000);
    return () => clearInterval(testimonialInterval);
  }, [currentTestimonial]);

  const steps = [
    {
      number: '01',
      title: 'Company Details Collection',
      description: 'We gather all necessary information—company name, registered office address, director and shareholder details, share capital, and business activities. Our experts ensure your company structure is optimized for your specific needs.'
    },
    {
      number: '02',
      title: 'Document Preparation & Review',
      description: 'We prepare all required incorporation documents including Memorandum of Association, Articles of Association, and registration forms. Everything is reviewed for accuracy and compliance with Companies House requirements before submission.'
    },
    {
      number: '03',
      title: 'Registration & Certificate',
      description: 'We submit your application electronically to Companies House and monitor its progress. Once approved (typically within 24 hours), you receive your Certificate of Incorporation, company number, and authentication code to start trading immediately.'
    }
  ];

  const usps = [
    {
      icon: <Clock className="w-8 h-8" />,
      title: '24-Hour Incorporation',
      description: 'Our streamlined electronic filing process with Companies House means your company is typically incorporated within 24 hours of submission. We handle everything efficiently so you can start trading immediately, giving you a critical head start in launching your business without unnecessary delays.'
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: 'Complete Compliance Handling',
      description: 'We ensure every detail meets Companies House requirements, from company name availability to proper share structure and statutory documents. Our expertise means your formation is done correctly the first time, avoiding costly rejections or amendment requirements that delay your business launch.'
    },
    {
      icon: <Building className="w-8 h-8" />,
      title: 'Optimized Company Structure',
      description: 'We don\'t just process paperwork—we advise on the optimal company structure for your specific situation. Whether you need a simple single-director company or a more complex multi-shareholder setup, we ensure your structure supports your business goals and future growth plans.'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Ongoing Support & Guidance',
      description: 'Company formation is just the beginning. We provide guidance on your ongoing compliance obligations, VAT registration if needed, and connections to accounting services. You\'re never left wondering what comes next—we support your business beyond just incorporation.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <Badge className="mb-4 bg-primary text-primary-foreground">Professional Company Formation Service</Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
                Company Formation Made Simple and Fast
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                Start your UK limited company with confidence. Our expert company formation service handles everything from choosing the right company name to filing with Companies House. We ensure full compliance with UK company law while getting you incorporated fast—typically within 24 hours. Whether you're a solo entrepreneur or forming a partnership, we streamline the entire process. At just £199, we make professional company formation accessible to everyone. Get your Certificate of Incorporation and start trading immediately with complete peace of mind.
              </p>
              
              {/* CTA Button */}
              <div className="flex gap-4">
                <Button 
                  onClick={openPopup}
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground group"
                >
                  Form Your Company
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
              <ServiceForm />
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Service */}
      <section className="py-20 px-4 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <img src="https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=800&h=600&fit=crop" alt="Company Formation" className="rounded-lg shadow-elegant-xl w-full h-auto" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">What Is Company Formation?</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Company formation is the legal process of creating a limited company and registering it with Companies House, the UK's official registrar of companies. Once formed, your company becomes a separate legal entity with its own identity, distinct from you as an individual. This provides crucial benefits: limited liability protection (your personal assets are separate from business debts), enhanced credibility with clients and suppliers, tax efficiency advantages, and the ability to raise investment capital. The formation process involves choosing and checking a company name, appointing directors and shareholders, deciding on share capital, preparing statutory documents (Memorandum and Articles of Association), and filing these with Companies House. Our service handles every step professionally, ensuring you meet all legal requirements while getting incorporated quickly. Most companies are approved within 24 hours, and you can start trading immediately upon receiving your Certificate of Incorporation.
              </p>
              
              {/* CTA Button */}
              <div className="mt-8">
                <Button 
                  onClick={openPopup}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground group"
                  size="lg"
                >
                  Form Your Company
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-20 px-4 bg-gradient-to-br from-secondary/5 to-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">How We Work: Our Company Formation Process</h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                We've simplified company formation into three clear steps. Our efficient process removes complexity and uncertainty, getting your company incorporated fast while ensuring everything is done correctly. From initial consultation to receiving your Certificate of Incorporation, we handle everything.
              </p>
            </motion.div>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}>
                <Card className="p-8 h-full border-2 border-transparent hover:border-primary transition-all duration-300 relative overflow-hidden group">
                  <div className="absolute top-0 right-0 text-8xl font-bold text-primary/5 group-hover:text-primary/10 transition-colors duration-300">{step.number}</div>
                  <div className="relative z-10">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center text-primary-foreground font-bold text-2xl mb-6 shadow-lg">{step.number}</div>
                    <h3 className="text-xl font-semibold text-foreground mb-4">{step.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
          
          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Button 
              onClick={openPopup}
              className="bg-secondary hover:bg-secondary/90 text-secondary-foreground group"
              size="lg"
            >
              Get Started Now
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 bg-background">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <Badge className="mb-4 bg-secondary/10 text-secondary border-secondary/20">Transparent Pricing</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">Simple Pricing for Company Formation</h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">Affordable company formation service for your business needs.</p>
            </motion.div>
          </div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <Card className="p-10 border-2 border-primary shadow-elegant-xl hover:shadow-2xl transition-all duration-300 relative overflow-hidden group bg-gradient-to-br from-primary/5 to-background">
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-full -mr-20 -mt-20 group-hover:scale-150 transition-transform duration-500"></div>
              
              <div className="relative z-10">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-8">
                  <div>
                    <h3 className="text-3xl font-bold text-foreground mb-2">Company Formation</h3>
                    <div className="flex items-baseline gap-2 mb-3">
                      <div className="text-5xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">From £200</div>
                    </div>
                    <Badge className="bg-primary/10 text-primary border border-primary/20">24/7 monitoring</Badge>
                  </div>
                </div>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-success mt-1 flex-shrink-0" />
                    <span className="text-base text-muted-foreground leading-relaxed">We handle the entire process, ensuring your company is set up quickly and in full compliance with UK regulations.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-success mt-1 flex-shrink-0" />
                    <span className="text-base text-muted-foreground leading-relaxed">Various formation options for you to choose from including LLCs, Corporations or Non-Profit organisation.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-success mt-1 flex-shrink-0" />
                    <span className="text-base text-muted-foreground leading-relaxed">Receive all essential paperwork and legal documents, ensuring a smooth and hassle-free company formation experience.</span>
                  </div>
                </div>
                
                <Button 
                  onClick={openPopup} 
                  className="w-full md:w-auto px-8 py-6 text-lg bg-gradient-to-r from-primary to-secondary hover:shadow-glow transition-all duration-300"
                >
                  Get Started - Form My Company
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">Why Choose Trademark Assist for Company Formation</h2>
              <p className="text-lg text-muted-foreground max-w-4xl mx-auto leading-relaxed">
                We've formed thousands of companies with expert guidance on UK company law, business structure, and compliance—ensuring your foundation supports long-term success from day one.
              </p>
            </motion.div>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {usps.map((usp, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}>
                <Card className="p-8 border-2 border-border hover:border-primary/50 transition-all duration-500 h-full group relative overflow-hidden bg-white hover:bg-gradient-to-br hover:from-primary/5 hover:to-secondary/5 hover:shadow-[0_20px_60px_-15px_rgba(1,33,105,0.3)] hover:scale-[1.02]">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out"></div>
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/20 to-transparent rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 -mr-16 -mt-16"></div>
                  <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-secondary/20 to-transparent rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 -ml-16 -mb-16"></div>
                  <div className="relative z-10">
                    <div className="relative w-20 h-20 mb-6 group-hover:mb-8 transition-all duration-500">
                      <div className="absolute inset-0 bg-gradient-to-br from-primary to-secondary rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500"></div>
                      <div className="relative w-20 h-20 bg-gradient-to-br from-primary/10 to-secondary/10 group-hover:from-primary group-hover:to-secondary rounded-2xl flex items-center justify-center text-primary group-hover:text-white transition-all duration-500 group-hover:rotate-[360deg] group-hover:scale-110 shadow-lg group-hover:shadow-2xl">
                        {usp.icon}
                      </div>
                    </div>
                    <h3 className="text-2xl font-semibold text-foreground mb-4 group-hover:text-primary transition-all duration-500 group-hover:tracking-wide">{usp.title}</h3>
                    <p className="text-muted-foreground leading-relaxed group-hover:text-foreground/90 transition-all duration-500">{usp.description}</p>
                    <div className="mt-6 h-1 w-0 group-hover:w-full bg-gradient-to-r from-primary to-secondary rounded-full transition-all duration-700 ease-out"></div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&h=600&fit=crop" alt="Company Formation" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary-dark/90 to-secondary/85"></div>
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-primary-foreground">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Start Your Business Today - Company Formation from £200</h2>
            <p className="text-lg mb-8 text-primary-foreground/90 leading-relaxed max-w-3xl mx-auto">
              Don't let complicated paperwork delay your business dreams. Our expert company formation service gets you incorporated fast—typically within 24 hours—with full confidence that everything is done correctly. From choosing your company name to receiving your Certificate of Incorporation, we handle everything. Start trading immediately with professional support every step of the way.
            </p>
            <Button onClick={openPopup} size="lg" variant="secondary" className="text-lg px-8 bg-primary-foreground text-primary hover:bg-primary-foreground/90 hover:scale-105 transition-all duration-300 shadow-elegant-xl">
              Form My Company Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-gradient-to-br from-secondary/5 to-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16"><h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">What Our Clients Say About Our Company Formation Service</h2></div>
          <div className="relative">
            <button onClick={prevTestimonial} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 z-10 bg-primary text-primary-foreground rounded-full p-3 shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300" aria-label="Previous testimonials"><ChevronLeft className="w-6 h-6" /></button>
            <button onClick={nextTestimonial} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 z-10 bg-primary text-primary-foreground rounded-full p-3 shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300" aria-label="Next testimonials"><ChevronRight className="w-6 h-6" /></button>
            <div className="overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div key={currentTestimonial} initial={{ opacity: 0, x: 100 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -100 }} transition={{ duration: 0.5, ease: "easeInOut" }} className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {getVisibleTestimonials().map((testimonial, index) => (<div key={`${testimonial.originalIndex}-${index}`}><TestimonialCard testimonial={testimonial} /></div>))}
                </motion.div>
              </AnimatePresence>
            </div>
            <div className="flex justify-center gap-2 mt-8">
              {Array.from({ length: Math.ceil(testimonials.length / 3) }).map((_, index) => (<button key={index} onClick={() => setCurrentTestimonial(index * 3)} className={`h-2 rounded-full transition-all duration-500 ease-out ${Math.floor(currentTestimonial / 3) === index ? 'bg-primary w-8 shadow-glow' : 'bg-border w-2 hover:bg-primary/50 hover:w-4'}`} aria-label={`View testimonial group ${index + 1}`} />))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 px-4 bg-background">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16"><h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">Frequently Asked Questions About Company Formation</h2></div>
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">How long does company formation take?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">Most companies are incorporated within 24 hours of submission to Companies House. Our electronic filing system ensures fast processing, and you'll typically receive your Certificate of Incorporation the next business day. In rare cases where additional checks are required, it might take 3-5 days. Our Premium package offers same-day priority processing for urgent formations. Once you receive your certificate, you can start trading immediately—there's no waiting period.</AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">What do I need to provide to form a company?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">You'll need to provide: your proposed company name (we'll check availability), a UK registered office address (we can provide this in Premium package), details of all directors and shareholders (names, dates of birth, residential addresses), share capital information (how many shares and their value), and a description of your business activities (SIC codes). You'll also need to confirm that directors are eligible to serve. We'll guide you through gathering all this information and explain what each requirement means for your specific situation.</AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">Do I need a business address to form a company?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">Yes, every UK company must have a registered office address in the same country where it's registered (England & Wales, Scotland, or Northern Ireland). This address appears on public record and is where Companies House sends official correspondence. It can be your home address, business premises, or an accountant's or solicitor's office. Our Premium package includes a professional registered office address service for one year if you prefer not to use your home address. This keeps your personal address private while ensuring compliance.</AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-4" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">What's the difference between a director and a shareholder?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">Directors run the company on a day-to-day basis—they make business decisions, sign contracts, and have legal responsibilities for company compliance. Shareholders own the company by holding shares—they have voting rights and receive dividends from profits. One person can be both a director and a shareholder (common in small companies), or these roles can be separate. All companies must have at least one director who is a real person (not another company). There's no minimum number of shareholders, and shareholders don't have to be individuals—they can be other companies or organizations.</AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-5" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">What happens after my company is formed?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">Once formed, you receive your Certificate of Incorporation (official proof your company exists), company number (used for all official correspondence), and authentication code (needed to file online with Companies House). You can then open a business bank account, register for Corporation Tax with HMRC (required within 3 months of starting trading), register for VAT if your turnover exceeds £85,000, set up PAYE if you have employees, and start trading. Our Standard and Premium packages include first-year compliance guidance to help you understand and meet all these obligations. We also provide connections to accounting services if needed.</AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default CompanyFormationPage;